/**
 * Utility function to sync MIS Checklist data to KPI Scorecard
 * This function calculates scores for all parameters based on MIS Checklist data
 */

import { FLSSafetySystemRow } from "../components/MISChecklist/FLSSafetySystemsTable";
import { MEPFLSInfrastructureRow } from "../components/MISChecklist/MEPFLSInfrastructureTable";
import { FireIncidentDrillsRow } from "../components/MISChecklist/FireIncidentDrillsTable";

export interface FireSafetyParameter {
  parameter: string;
  misChecklistSrNos?: number | number[]; // Single or multiple Sr.Nos from MIS Checklist for matching
  inPercent: number | null;
  score: number | null;
  maxScore: number;
  percentage: number | null;
}

export interface SyncResult {
  success: boolean;
  scorecardId?: string;
  message: string;
}

/**
 * Calculate scores for a single parameter based on MIS Checklist data
 */
export const calculateParameterScore = (
  param: FireSafetyParameter,
  flsSafetySystems: FLSSafetySystemRow[],
  mepFLSInfrastructure: MEPFLSInfrastructureRow[],
  fireIncidentDrills: FireIncidentDrillsRow[]
): FireSafetyParameter => {
  // Primary: Use misChecklistSrNos if available
  if (
    param.misChecklistSrNos !== undefined &&
    param.misChecklistSrNos !== null
  ) {
    const srNosToMatch = Array.isArray(param.misChecklistSrNos)
      ? param.misChecklistSrNos
      : [param.misChecklistSrNos];

    let totalInstalled = 0;
    let totalWorking = 0;
    let installedForNullWorking = 0; // Track installed count for rows where working is null (N/A)
    let hasInstalledValue = false; // Track if we have any installed value (not null)
    let hasAnyWorkingValue = false; // Track if we have ANY working value (0 or > 0, not null)
    let foundMatch = false;

    // Check Table 1: Fire and Life Safety Equipment (Sr.No 1-20)
    const matchingFromTable1 = flsSafetySystems.filter((item) =>
      srNosToMatch.includes(item.srNo)
    );
    matchingFromTable1.forEach((item) => {
      foundMatch = true;
      if (item.installed !== null && item.installed !== undefined) {
        totalInstalled += item.installed;
        hasInstalledValue = true;

        // If working is null (N/A), count installed towards full score
        if (item.working === null || item.working === undefined) {
          installedForNullWorking += item.installed;
        }
      }
      if (item.working !== null && item.working !== undefined) {
        totalWorking += item.working;
        hasAnyWorkingValue = true;
      }
    });

    // Check Table 2: Fire and Life Safety System (Sr.No 21-44)
    const matchingFromTable2 = mepFLSInfrastructure.filter((item) =>
      srNosToMatch.includes(item.srNo)
    );
    matchingFromTable2.forEach((item) => {
      foundMatch = true;
      if (item.totalInstalledNos !== null && item.totalInstalledNos !== undefined) {
        totalInstalled += item.totalInstalledNos;
        hasInstalledValue = true;

        // If workingNos is null (N/A), count installed towards full score
        if (item.workingNos === null || item.workingNos === undefined) {
          installedForNullWorking += item.totalInstalledNos;
        }
      }
      if (item.workingNos !== null && item.workingNos !== undefined) {
        totalWorking += item.workingNos;
        hasAnyWorkingValue = true;
      }
    });

    // Check Table 3: Fire Incident/Drills (Sr.No 45-49)
    // For this table, we use actualNo as "working" and targetNo as "installed"
    const matchingFromTable3 = fireIncidentDrills.filter((item) =>
      srNosToMatch.includes(Number(item.srNo))
    );
    matchingFromTable3.forEach((item) => {
      foundMatch = true;
      if (item.targetNo !== null && item.targetNo !== undefined) {
        totalInstalled += item.targetNo;
        hasInstalledValue = true;

        // If actualNo is null (N/A), count targetNo towards full score
        if (item.actualNo === null || item.actualNo === undefined) {
          installedForNullWorking += item.targetNo;
        }
      }
      if (item.actualNo !== null && item.actualNo !== undefined) {
        totalWorking += item.actualNo;
        hasAnyWorkingValue = true;
      }
    });

    if (!foundMatch) {
      // No matching data found, keep original score
      return param;
    }

    // Calculate score based on N/A vs 0 logic:
    // - If working/inspected/actual is null (N/A): award full score for that item
    // - If working/inspected/actual is 0 (explicit zero): calculate normally (0 score)
    // - If working/inspected/actual has a value > 0: calculate normally
    let calculatedScore: number | null = null;

    if (!hasInstalledValue) {
      // No installed values at all - award full score (N/A)
      calculatedScore = param.maxScore;
    } else if (totalInstalled > 0) {
      // We have installed values
      if (!hasAnyWorkingValue) {
        // All working values are null (N/A) - award full score
        calculatedScore = param.maxScore;
      } else {
        // We have at least one numeric working value (could be 0 or > 0)
        // Calculate: (working + installedForNullWorking) / totalInstalled * maxScore
        // This means: items with null working get full score, items with 0 get 0, items with > 0 get proportional
        const effectiveWorking = totalWorking + installedForNullWorking;
        calculatedScore = (effectiveWorking / totalInstalled) * param.maxScore;
        calculatedScore = Math.round(calculatedScore * 100) / 100; // Round to 2 decimal places
      }
    }

    // Calculate derived fields
    const inPercent =
      calculatedScore !== null && param.maxScore > 0
        ? calculatedScore / param.maxScore
        : 0;
    const percentage =
      calculatedScore !== null && param.maxScore > 0
        ? (calculatedScore / param.maxScore) * 100
        : 0;

    return {
      ...param,
      score: calculatedScore,
      inPercent: Math.round(inPercent * 100) / 100,
      percentage: Math.round(percentage * 100) / 100,
    };
  }

  // No misChecklistSrNos, keep original parameter
  return param;
};

/**
 * Generate initial Fire Safety Parameters with default values
 */
export const getInitialFireSafetyParameters = (): FireSafetyParameter[] => [
  {
    parameter: "EXTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED",
    inPercent: 0,
    score: null,
    maxScore: 15,
    percentage: 0,
  },
  {
    parameter: "INTERNAL AUDIT HIGH CATEGORY POINTS COMPLETED",
    inPercent: 0,
    score: null,
    maxScore: 10,
    percentage: 0,
  },
  {
    parameter:
      "NO.OF SMOKE, HEAT DETECTORS INSPECTED, TESTED & FOUND HEALTHY (3)",
    misChecklistSrNos: 3,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter: "FIRE ALARM PANEL & INTERNAL BATTERY HEALTHINESS (1)",
    misChecklistSrNos: 1,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "NO. OF. MANUAL CALL POINTS TESTED & FOUND TO WORKING (5)",
    misChecklistSrNos: 5,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "ARE THE FIRE HYDRANT, SPRINKLER SPUMP AND DIESEL ENGINE FIRE PUMP ARE ADEQUATELY DESIGNED & INSTALLED AS PER NBCS 2026 & LOCAL REGULATIONS (21)",
    misChecklistSrNos: 21,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "IS FIRE WATER UNDERGROUND TANK AND OVERHEAD TANK ARE OF ADEQUATE CAPACITY (22)",
    misChecklistSrNos: 22,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "ARE SPRINKLER SYSTEM PROVIDED IN ALL THE FLOORS AND FOUND ADEQUATE (35)",
    misChecklistSrNos: 35,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "ARE NO.OF INTERNAL HYDRANT, HOSE REEL, EXTERNAL HYDRANT INSPECTION & FLOW TEST DONE (44)",
    misChecklistSrNos: 44,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "IS PANEL MOUNT CLEAN AGENT SUPPRESSION SYSTEM OR FIXED GAS SUPPRESSION SYSTEM AVAILABLE IN THE ELECTRICAL PANEL ROOM AND UPS /BATTERY ROOM (23)",
    misChecklistSrNos: 23,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "NO.OF EXTINGUISHERS (INCLUDING MODULAR & GAS SUPRESSION) SYSTEM MONTHLY INSPECTED & AMC/ PM (6)",
    misChecklistSrNos: 6,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter:
      "USE OF KITCHEN HOOD SUPRESSION SYSTEM & K-TYPE EXTINGUISHER (50)",
    misChecklistSrNos: 50,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "FIRE PUMP DAILY TRIAL RUN & PM - HEALTHINESS (41)",
    misChecklistSrNos: 41,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "FIRE HYDRANT PUMPS SET IN AUTO MODE (41)",
    misChecklistSrNos: 41,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "FIRE HYDRANT PRESSURE GAUGE & SWITCHES CALIBRATED (11)",
    misChecklistSrNos: 11,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter: "ARE SPRINKLER SYSTEM PROVIDED IN ALL THE LOCATIONS AND ALSO IN CRITICAL AREAS LIKE ALL ICU, MRI AND OT OR ALTERNATIVELY MIST SYSTM OR SUPRESSION SYSTEM AVAILABLE. (24)",
    misChecklistSrNos: 24,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter: "ARE SMOKE DETECTOR AND SPRINKLER SYSTEM PROVIDED ABOVE FALSE CEILING (IF GAP BETWEEN FALSE & TRUE CEILING EXCEEDS 800 MM) & ARE THEY INSPECTED (25)",
    misChecklistSrNos: 25,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "NO. OF SPRINKLERS TESTED & VALVE OPEN CONDITION IN EACH FLOOR (8)",
    misChecklistSrNos: 8,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "IS FIRE RATED DOOR PROVIDED FOR ALL ELECTRICAL PANEL ROOM  UPS ROOM  ICU, OT, MRI (26)",
    misChecklistSrNos: 26,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "IS FIRE RATED DOOR PROVIDED FOR ALL  ICU, OT, MRI & WARDS WHERE NON AMBULATORY PATIENTS ARE AVAILABLE TO SELF PROTECT THEMSELVES (27)",
    misChecklistSrNos: 27,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "NO OF FIRE & SMOKE COMPARTMENTATION DOOR INSPECTION (59)",
    misChecklistSrNos: 59,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter: "PUBLIC ADDRESSING SYSTEM INSPECTED (16)",
    misChecklistSrNos: 16,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "PROVISION OF STAIRCASE PRESSURISATION SYSTEM (MINIMUM 50 PA) FOR ALL INTERNAL STAIRCASE AVAILABLE /INSPECTED OR NATURALLY VENTILILATED OR CROSS VENTILATION (19)",
    misChecklistSrNos: 19,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "PROVISION OF LIFT LOBBY PRESSURIZATION (MINIMUM 25 PA), IF LIFT LOBBY FIRE SEGREGATION AND COMPARTMENTATION DONE THROUGH FIRE DOOR (28)",
    misChecklistSrNos: 28,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter: "IS LIFT LOBBY FIRE COMPARTMENTED IN FLOOR AREA & BASEMENT, IF NOT THEN IS LIFT  WELL SHAFT PRESSURIZED WITH MINIMUM 50 PA (29)",
    misChecklistSrNos: 29,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter: "IS LIFT LOBBY COMPARTMENTED IN THE BASEMENT AREA AND PROVIDED WITH PRESSURIZATION OF MINIMUM 25 PA (30)",
    misChecklistSrNos: 30,
    inPercent: 0,
    score: null,
    maxScore: 1,
    percentage: 0,
  },
  {
    parameter:
      "IS SMOKE EXTRACTION SYSTEM PROVIDED IN THE FLOOR CORRIDOR & BASEMENT WITH MINIMUM 12 AIR CHANGES PER HOUR AND SMOKE EXTRACTION INTERLOCKED WITH FIRE ALARM PANEL. (20)",
    misChecklistSrNos: 20,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter:
      "ARE FIRE DAMPERS PROVIDED IN THE AHU DUCTS IN SUPPLY AND RETURN AIR SIDE (31)",
    misChecklistSrNos: 31,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "ARE OTHER FIRE ALARM PANEL INTERLOCKS SUCH AS AHU TRIPPING ON DETECTOR ACTIVATION,OPERATION OF AHU DAMPERS, HOUSING OF LIFT, ACCESS DOOR DEACTIVATION CHECKED AND VERIFIED ? (12)",
    misChecklistSrNos: 12,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "FIRE LICENSE (32)",
    misChecklistSrNos: 32,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "NO.OF EMERGENCY BACKUP SYSTEMS, SIGNAGES & LIGHTS CHECKED (90 MINUTES BACK FOR EMERGENCY LIGHT) (56)",
    misChecklistSrNos: 56,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "NO. OF FIREMAN LIFT ADEQUATE & LIFTS CHECKED FOR ARD TALK BACK SYSTEM HEALTHINESS.(EACH BLOCK OR WING SHALL HAVE MINIMUM 1 NO. OF FIRE LIFT) (14)",
    misChecklistSrNos: 14,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter: "ARE RAMPS PROVIDED & WIDTH OF 2.4 METRES MAINTAINED (33)",
    misChecklistSrNos: 33,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "ARE CRITICAL LIFE SAVING EVACUATION EQUIPMENT LIKE SCBA, FIRE SUIT, STAIR CLLIMBING WHEEL CHAIR, PATIENT STRETCHER AND EVACUATION MATTRESS AVAILABLE & MAINTAINED IN HEALTHY CONDITION (34)",
    misChecklistSrNos: 34,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter: "NO. OF NEAR MISS INCIDENTS REPORTED (60)",
    misChecklistSrNos: 60,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter: "NO. OF MAJOR ACCIDENTS REPORTED (61)",
    misChecklistSrNos: 61,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter:
      "PERIODICAL TRAINING PROGRAMME ON FIRE SAFETY, FIRE EQUIPMENT USAGE AND MAINTENANCE. (ONCE IN 6 MONTHS) - 2 NOS / YEAR (62)",
    misChecklistSrNos: 62,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
  {
    parameter:
      "NO. OF INTERNAL FIRE DRILLS COMPLETED FOR MINIMUM 8 DIFFERENT SCENARIOS ONCE IN 6 MONTHS (FIRE IN ELECTRICAL / UPS PANEL ROOM, FIRE IN ICU, FIRE IN OT, FIRE IN HAZMAT AREA, FIRE IN DIESEL AREA / DG, FIRE IN RADIOLOGY, FIRE IN MGPS/OXYGEN AREA & FIRE IN MRI ) - TOTAL 16 NOS. OF DRILLS / YEAR (63)",
    misChecklistSrNos: 63,
    inPercent: 0,
    score: null,
    maxScore: 3,
    percentage: 0,
  },
  {
    parameter:
      "NO.OF EXTERNAL GOVT FIRE & RESCUE DEPARTMENT FIRE DRILLS (ONCE IN A YEAR ) (64)",
    misChecklistSrNos: 64,
    inPercent: 0,
    score: null,
    maxScore: 2,
    percentage: 0,
  },
];

export interface AuditRecord {
  category: string;
  totalNC: number | null;
  completionNC: number | null;
  pending: number | null;
  completionPercent: number | null;
  currentScore: number | null;
  maxScore: number;
}

/**
 * Get initial External Audit Records
 * This is the single source of truth for external audit record templates
 */
export const getInitialExternalAuditRecords = (): AuditRecord[] => [
  {
    category: "A",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 15,
  },
  {
    category: "B",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 10,
  },
  {
    category: "C",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 5,
  },
];

/**
 * Get initial Internal Audit Records
 * This is the single source of truth for internal audit record templates
 */
export const getInitialInternalAuditRecords = (): AuditRecord[] => [
  {
    category: "A",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 10,
  },
  {
    category: "B",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 5,
  },
  {
    category: "C",
    totalNC: null,
    completionNC: null,
    pending: null,
    completionPercent: null,
    currentScore: null,
    maxScore: 2.5,
  },
];

/**
 * Generate auto name for KPI Scorecard based on date
 */
export const generateKPIScorecardName = (date: string | null): string => {
  if (!date) {
    const today = new Date();
    const dateStr = today.toISOString().split("T")[0];
    return `Fire Safety Scorecard - ${dateStr}`;
  }
  return `Fire Safety Scorecard - ${date}`;
};

